import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useQuery, useQueryClient } from "react-query";
import { Loading } from "../../UIKit/Loading";
import { CenterInScreen } from "../../UIKit/CenterInScreen";
import { Button } from "../../UIKit/Button";
import { PaginatedTable, tableColumn } from "../../UIKit/Table";
import RoutesExternalResources from "./Routes";
import { FormattedMessage, useIntl } from "react-intl";
import { Modal } from "../../UIKit/Modal";
import { ExternalResource, ExternalResourceLibrary } from "./ExternalResource";
import { contentPageResponse } from "../commonTypes";

interface ExternalResourcesListProps {
  library: ExternalResourceLibrary;
  columns: tableColumn<ExternalResource>[];
  clientId: string;
  supplierId: string;
}

export function ExternalResourcesList(props: ExternalResourcesListProps) {
  const itemsPerPage = 20;
  const history = useHistory();
  const intl = useIntl();
  const [startingAt, setStartingAt] = useState<number>(1);
  const [resourceToDelete, triggerDeleteModal] = useState<
    ExternalResource | false
  >(false);
  const queryClient = useQueryClient();
  const {
    isFetching: resourcesAreFetching,
    data: resourcesPage,
    refetch,
  } = useQuery<contentPageResponse<ExternalResource>>(
    "externalResources::all::page::" + startingAt,
    () =>
      props.library.findPaginated(
        (startingAt - 1) * itemsPerPage + 1,
        itemsPerPage
      ),
    { keepPreviousData: true }
  );

  function deleteResource(resource: ExternalResource) {
    props.library.delete(resource).then((success) => {
      if (success) {
        refetch();
        triggerDeleteModal(false);
      }
    });
  }

  if (!resourcesPage) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center">
        <Loading
          label={intl.formatMessage({
            id: "externalResources.checking",
            defaultMessage: "Loading external resources",
          })}
        />
      </div>
    );
  }

  const resources = resourcesPage.items;
  const resourcesStats = resourcesPage.stats;

  return (
    <>
      {resourceToDelete && (
        <Modal
          onCancel={() => triggerDeleteModal(false)}
          onConfirm={() => deleteResource(resourceToDelete)}
          altButtonLabel="cancel"
          mainButtonLabel="delete"
        >
          <div>
            <p className="font-bold text-xl">
              <FormattedMessage
                id="externalResources.delete.warning.title"
                defaultMessage="Delete an external resource"
              />
            </p>
            <p>
              <FormattedMessage
                id="externalResources.delete.warning.body"
                defaultMessage="This suppression is irreversible"
              />
            </p>
          </div>
        </Modal>
      )}
      {resources.length === 0 ? (
        <CenterInScreen>
          <div className="text-center mb-2">
            <p>
              <FormattedMessage
                id="externalResources.no.item.yet.1"
                defaultMessage="...no external resource referenced at this time 🤷"
              />
            </p>
            <p>
              <FormattedMessage
                id="externalResources.no.item.yet.2"
                defaultMessage="Add your first external resource to get started"
              />
            </p>
            <div className="pt-2">
              <Button
                iconName="PlusIcon"
                label={"externalResources.create.first.cta"}
                type="primary"
                onClick={() =>
                  history.push(
                    RoutesExternalResources(props.clientId, props.supplierId)
                      .add
                  )
                }
              />
            </div>
          </div>
        </CenterInScreen>
      ) : (
        <div>
          <div className="flex justify-between mb-4 ml-2">
            <h2 className="text-xl font-bold">
              <FormattedMessage
                id="externalResources.count.plural"
                defaultMessage="{num, plural, one {# resource} other {# resources}}"
                values={{ num: resourcesStats.totalCount }}
              />
            </h2>
            {resourcesAreFetching && (
              <div className="items-center">
                <Loading
                  label={intl.formatMessage({
                    id: "externalResources.checking",
                    defaultMessage: "Loading external resources",
                  })}
                />
              </div>
            )}
          </div>
          <PaginatedTable<ExternalResource>
            onPageChange={setStartingAt}
            totalItemsCount={resourcesStats.totalCount || 0}
            itemsPerPage={itemsPerPage}
            data={resources}
            page={startingAt}
            columns={[
              ...props.columns,
              {
                label: "actions",
                content: (resource) => (
                  <div className="flex flex-row">
                    <div className="mr-1">
                      <Button
                        size="small"
                        iconName="PencilIcon"
                        onClick={() =>
                          history.push(
                            RoutesExternalResources(
                              props.clientId,
                              props.supplierId
                            ).edit(resource.id)
                          )
                        }
                        label={"edit"}
                      />
                    </div>
                    <Button
                      size="tiny"
                      type="cancel"
                      iconName="XIcon"
                      onClick={() => triggerDeleteModal(resource)}
                      label={intl.formatMessage({
                        id: "forms.delete",
                        defaultMessage: "delete",
                      })}
                    />
                  </div>
                ),
              },
            ]}
          />
          <div className="text-center mb-2 mt-4">
            <Button
              iconName="PlusIcon"
              label={intl.formatMessage({
                id: "externalResources.add.a.resource",
                defaultMessage: "Add an external resource",
              })}
              onClick={() =>
                history.push(
                  RoutesExternalResources(props.clientId, props.supplierId).add
                )
              }
            />
          </div>
        </div>
      )}
    </>
  );
}
